import { CheckBoxOutlined, CheckCircle, Close, DeleteOutline, ExpandLess, ExpandMore, ErrorOutline } from '@mui/icons-material';
import { Checkbox } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOcrQueue } from '@practium/features/analyses';
import { ConfirmModal, FloraAIIcon, useToast } from '@practium/ui';
import styles from './OcrQueueWidget.module.css';

export const OcrQueueWidget = () => {
	const navigate = useNavigate();
	const toast = useToast();
	const { items, remove, removeMany, isOpen, setIsOpen } = useOcrQueue();
	const [selectionMode, setSelectionMode] = useState(false);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const selectedItems = items.filter((item) => selectedIds.includes(item.id));
	const selectedCount = selectedItems.length;
	const allSelected = items.length > 0 && selectedCount === items.length;
	const toggleItem = (id: string) => setSelectedIds((current) =>
		current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id]
	);
	const knownItemIds = useRef(new Set(items.map((item) => item.id)));
	const notifiedErrorIds = useRef(
		new Set(items.filter((item) => item.status === 'error').map((item) => item.id))
	);
	const notifiedDoneIds = useRef(
		new Set(items.filter((item) => item.status === 'done').map((item) => item.id))
	);
	const finished = items.filter((item) => item.status === 'done').length;
	const isProcessing = items.some((item) =>
		['uploading', 'queued', 'processing'].includes(item.status)
	);
	const hasError = items.some((item) => item.status === 'error');
	const floraStatus = isProcessing
		? `A Flora está analisando ${items.length === 1 ? 'seu PDF' : 'seus PDFs'}`
		: hasError
			? 'A Flora encontrou um problema'
			: 'A Flora concluiu as análises';

	useEffect(() => {
		const addedItems = items.filter((item) => !knownItemIds.current.has(item.id));
		if (addedItems.length === 0) return;
		
		addedItems.forEach((item) => knownItemIds.current.add(item.id));
		toast.dismiss('info');
		toast.info(
			addedItems.length === 1
				? `PDF ${addedItems[0].fileName} carregado. A Flora iniciou a análise.`
				: `${addedItems.length} PDFs carregados. A Flora iniciou as análises.`
		);
	}, [items, toast]);

	useEffect(() => {
		const completedItems = items.filter(
			(item) => item.status === 'done' && !notifiedDoneIds.current.has(item.id)
		);

		completedItems.forEach((item) => {
			notifiedDoneIds.current.add(item.id);
			toast.dismiss('success');
			toast.success(`PDF ${item.fileName} carregado e analisado com sucesso!`);
		});
	}, [items, toast]);

	useEffect(() => {
		const newErrors = items.filter(
			(item) => item.status === 'error' && !notifiedErrorIds.current.has(item.id)
		);

		newErrors.forEach((item) => {
			notifiedErrorIds.current.add(item.id);
			toast.dismiss('error');
			toast.error(
				`Não foi possível analisar ${item.fileName}: ${item.error || 'o processamento do PDF falhou.'}`
			);
		});
	}, [items, toast]);

	useEffect(() => {
		if (items.length === 0) {
			setSelectionMode(false);
			setSelectedIds([]);
			setConfirmOpen(false);
		}
	}, [items.length]);

	if (items.length === 0) return null;

	const openItem = (item: (typeof items)[number]) => {
		if (item.status !== 'done' || !item.samples?.length) return;
		toast.dismiss('info');
		toast.info(`Abrindo PDF ${item.fileName}`);
		navigate(`/analises/novo?type=${item.analysisType === 'soil' ? 'soil' : 'plant'}`, {
			state: { ocrItemId: item.id },
		});
	};

	if (!isOpen) {
		return (
			<button
				className={styles.collapsed}
				onClick={() => setIsOpen(true)}
				aria-label="Abrir fila de OCR"
			>
				<FloraAIIcon
					className={`${styles.collapsedFlora} ${isProcessing ? styles.floraWorking : ''}`}
					aria-hidden="true"
				/>
				<span className={styles.progressCount}>
					{finished} de {items.length}
				</span>
				<ExpandLess />
			</button>
		);
	}

	return (
		<section className={styles.panel} aria-label="Processamento de laudos">
			<header className={styles.header}>
				<div className={styles.floraIdentity}>
					<div className={styles.floraAvatar} aria-hidden="true">
						<FloraAIIcon
							className={`${styles.floraIcon} ${isProcessing ? styles.floraWorking : ''}`}
						/>
					</div>
					<div className={styles.headerText}>
						<span className={styles.floraStatus} role="status" aria-live="polite">
							{floraStatus}
						</span>
						<div
							className={styles.progressSummary}
							role="status"
							aria-live="polite"
							aria-label={`${finished} de ${items.length} carregados`}
						>
							{isProcessing && <span className={styles.progressLoading} aria-hidden="true" />}
							<strong className={styles.progressCount} aria-hidden="true">
								{finished} de {items.length}
							</strong>
							<span className={styles.progressLabel} aria-hidden="true">
								carregados
							</span>
						</div>
					</div>
				</div>
				<button
					type="button"
					className={styles.selectionToggle}
					aria-pressed={selectionMode}
					onClick={() => {
						setSelectionMode((current) => !current);
						setSelectedIds([]);
						setConfirmOpen(false);
					}}
				>
					<CheckBoxOutlined />
					{selectionMode ? 'Cancelar' : 'Selecionar'}
				</button>
				<button
					className={styles.iconButton}
					onClick={() => setIsOpen(false)}
					aria-label="Minimizar fila"
				>
					<ExpandMore />
				</button>
			</header>
			{selectionMode && (
				<div className={styles.selectionToolbar}>
					<label className={styles.selectAll}>
						<Checkbox
							className={styles.checkbox}
							checked={allSelected}
							indeterminate={selectedCount > 0 && !allSelected}
							onChange={() => setSelectedIds(allSelected ? [] : items.map((item) => item.id))}
							size="small"
						/>
						<span>Selecionar todos ({items.length})</span>
					</label>
					<button
						type="button"
						className={styles.deleteSelected}
						disabled={selectedCount === 0}
						onClick={() => setConfirmOpen(true)}
					>
						<DeleteOutline fontSize="small" />
						Excluir selecionados ({selectedCount})
					</button>
				</div>
			)}
			<div className={styles.list}>
				{items.map((item) => (
					<div
						key={item.id}
						className={`${styles.item} ${item.status === 'done' ? styles.ready : ''} ${item.status === 'error' ? styles.errorItem : ''} ${selectionMode && selectedIds.includes(item.id) ? styles.selectedItem : ''}`}
					>
						{selectionMode && (
							<Checkbox
								className={styles.checkbox}
								checked={selectedIds.includes(item.id)}
								onChange={() => toggleItem(item.id)}
								slotProps={{ input: { 'aria-label': `Selecionar ${item.fileName}` } }}
								size="small"
							/>
						)}
						{item.status === 'done' && <CheckCircle className={styles.success} />}
						{item.status === 'error' && <ErrorOutline className={styles.error} />}
						{(item.status === 'uploading' ||
							item.status === 'queued' ||
							item.status === 'processing') && (
							<span className={styles.loading} role="status" aria-label="Carregando" />
						)}
						<div className={styles.fileDetails}>
							<span className={styles.fileName} title={item.fileName}>{item.fileName}</span>
							<span className={styles.fileMeta}>
								{item.fileSize !== undefined && `${(item.fileSize / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} MB • `}
								{item.fileName.includes('.') ? item.fileName.split('.').pop()?.toUpperCase() : 'Arquivo'}
							</span>
						</div>
						{item.status === 'done' && (
							<button type="button" className={styles.openText} onClick={() => openItem(item)}>Abrir</button>
						)}
						<button
							type="button"
							className={styles.removeButton}
							onClick={(event) => {
								event.stopPropagation();
								remove(item.id);
								toast.dismiss('success');
								toast.success(`PDF ${item.fileName} foi removido da fila.`);
							}}
							aria-label={`Excluir ${item.fileName}`}
							title="Excluir da fila"
						>
							<Close />
						</button>
					</div>
				))}
			</div>
			<ConfirmModal
				isOpen={confirmOpen && selectedCount > 0}
				title="Excluir arquivos selecionados?"
				message={`Essa ação irá remover ${selectedCount} ${selectedCount === 1 ? 'arquivo' : 'arquivos'} da lista. Não é possível desfazer.`}
				confirmText={`Excluir ${selectedCount} ${selectedCount === 1 ? 'arquivo' : 'arquivos'}`}
				cancelText="Cancelar"
				variant="danger"
				onCancel={() => setConfirmOpen(false)}
				onConfirm={() => {
					removeMany(selectedItems.map((item) => item.id));
					setSelectionMode(false);
					setSelectedIds([]);
					setConfirmOpen(false);
					toast.dismiss('info');
					toast.success(`${selectedCount} ${selectedCount === 1 ? 'arquivo removido' : 'arquivos removidos'} da fila.`);
				}}
			/>
		</section>
	);
};
